import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import PlayerStatsTable from './PlayerStatsTable';

const TeamComparison = ({ league_id }) => {
  const timeframeOptions = [
    { value: "SEASON", label: "Season" },
    { value: "LAST_30", label: "Last 30" },
    { value: "LAST_15", label: "Last 15" },
    { value: "LAST_7", label: "Last 7" },
  ];

  const [timeframe, setTimeframe] = useState(timeframeOptions[0]);
  const [rosters, setRosters] = useState({});
  const [loading, setLoading] = useState(true);
  const [team1, setTeam1] = useState(null);
  const [team2, setTeam2] = useState(null);
  const [teamOptions, setTeamOptions] = useState([]);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchRosters(timeframe.value);
  }, [league_id]);

  const fetchRosters = async (selectedTimeframe) => {
    try {
      const response = await fetch(`/get_rosters/${league_id}/${selectedTimeframe}`);
      if (!response.ok) throw new Error('Failed to fetch rosters');
      const data = await response.json();
      setRosters(data);

      // Create team options from the data
      const options = Object.keys(data).map(teamName => ({
        value: teamName,
        label: teamName
      }));
      setTeamOptions(options);

      // Auto-select first two teams if none selected
      if (!team1 && options.length > 0) setTeam1(options[0]);
      if (!team2 && options.length > 1) setTeam2(options[1]);
    } catch (error) {
      console.error("Error fetching roster data:", error);
      setRosters({});
    } finally {
      setLoading(false);
    }
  };

  const handleTimeframeChange = (option) => {
    setTimeframe(option);
    fetchRosters(option.value);
  };

  // Calculate team z-score totals
  const calculateTeamTotals = (players) => {
    const statColumns = [
      { key: 'fg_percentage', label: 'FG%', zScoreKey: 'fg_impact' },
      { key: 'ft_percentage', label: 'FT%', zScoreKey: 'ft_impact' },
      { key: 'three_pm', label: '3PM', zScoreKey: 'three_pm' },
      { key: 'rebounds', label: 'REB', zScoreKey: 'rebounds' },
      { key: 'assists', label: 'AST', zScoreKey: 'assists' },
      { key: 'steals', label: 'STL', zScoreKey: 'steals' },
      { key: 'blocks', label: 'BLK', zScoreKey: 'blocks' },
      { key: 'turnovers', label: 'TO', zScoreKey: 'turnovers' },
      { key: 'points', label: 'PTS', zScoreKey: 'points' },
    ];

    const totals = {};
    const validPlayers = players.filter(p => p.name);

    statColumns.forEach(col => {
      const sum = validPlayers.reduce((acc, player) => {
        return acc + (player.z_scores[col.zScoreKey] || 0);
      }, 0);
      totals[col.zScoreKey] = sum;
    });

    return { totals, statColumns };
  };

  const getZScoreColor = (value) => {
    const normalized = Math.max(-3, Math.min(3, value)) / 3;
    if (normalized > 0) {
      return `rgba(0, 255, 0, ${normalized * 0.3})`;
    } else {
      return `rgba(255, 0, 0, ${Math.abs(normalized) * 0.3})`;
    }
  };

  const formatValue = (value) => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return value;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // Calculate totals for both teams
  const team1Totals = team1 ? calculateTeamTotals(rosters[team1.value] || []) : null;
  const team2Totals = team2 ? calculateTeamTotals(rosters[team2.value] || []) : null;

  return (
    <div id="team-comparison">
      <div className="pure-g center">
        <div className="pure-u-1-1">
          <header>
            <h1>Team Comparison</h1>
          </header>
          <p className="description-text">
            Compare two teams side-by-side using z-scores to identify strengths and weaknesses
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="pure-g center" style={{ marginBottom: '20px' }}>
        <div className="pure-u-1-1">
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Team 1:
              </label>
              <Select
                options={teamOptions}
                value={team1}
                onChange={setTeam1}
                isSearchable={false}
              />
            </div>
            <div style={{ minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Team 2:
              </label>
              <Select
                options={teamOptions}
                value={team2}
                onChange={setTeam2}
                isSearchable={false}
              />
            </div>
            <div style={{ minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Timeframe:
              </label>
              <Select
                options={timeframeOptions}
                value={timeframe}
                onChange={handleTimeframeChange}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Summary Table */}
      {team1 && team2 && team1Totals && team2Totals && (
        <div className="pure-g center" style={{ marginBottom: '30px' }}>
          <div className="pure-u-1-1">
            <h2 style={{ textAlign: 'center' }}>Team Z-Score Comparison</h2>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <table className="pure-table pure-table-bordered" style={{ maxWidth: '700px', margin: '0 auto' }}>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th style={{ textAlign: 'center' }}>{team1.label}</th>
                    <th style={{ textAlign: 'center' }}>{team2.label}</th>
                  </tr>
                </thead>
                <tbody>
                  {team1Totals.statColumns.map(col => {
                    const t1Value = team1Totals.totals[col.zScoreKey];
                    const t2Value = team2Totals.totals[col.zScoreKey];
                    const winner = t1Value > t2Value ? team1.label :
                                   t2Value > t1Value ? team2.label :
                                   'Tie';

                    return (
                      <tr key={col.zScoreKey}>
                        <td style={{ fontWeight: 'bold' }}>{col.label}</td>
                        <td
                          style={{
                            textAlign: 'center',
                            backgroundColor: getZScoreColor(t1Value),
                            fontWeight: winner === team1.label ? 'bold' : 'normal'
                          }}
                        >
                          {formatValue(t1Value)}
                        </td>
                        <td
                          style={{
                            textAlign: 'center',
                            backgroundColor: getZScoreColor(t2Value),
                            fontWeight: winner === team2.label ? 'bold' : 'normal'
                          }}
                        >
                          {formatValue(t2Value)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Toggle for Player Details */}
      <div className="pure-g center" style={{ marginBottom: '20px' }}>
        <div className="pure-u-1-1" style={{ textAlign: 'center' }}>
          <label style={{ fontSize: '16px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showDetails}
              onChange={(e) => setShowDetails(e.target.checked)}
              style={{ marginRight: '10px', transform: 'scale(1.2)' }}
            />
            Show Player Details
          </label>
        </div>
      </div>

      {/* Side-by-side player comparison (only if toggled) */}
      {showDetails && team1 && team2 && (
        <div className="pure-g" style={{ gap: '20px' }}>
          <div className="pure-u-1-2" style={{ paddingRight: '10px' }}>
            <PlayerStatsTable
              players={rosters[team1.value] || []}
              teamName={team1.label}
              showTeamTotals={true}
              showStats={false}
            />
          </div>
          <div className="pure-u-1-2" style={{ paddingLeft: '10px' }}>
            <PlayerStatsTable
              players={rosters[team2.value] || []}
              teamName={team2.label}
              showTeamTotals={true}
              showStats={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamComparison;
